import React from 'react'

import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { INVOICE_STATUS_DISPLAY } from 'settings/constants/invoice'
import { createDateFromString, formatDateToFEFormatDateFns } from 'utils/date'
import { getPricingPeriod } from 'utils/service'

/**
 * Meta lookup rows arrive from the filters endpoint, which types nothing and omits fields per row —
 * hence the optional everything and the `||` chains below rather than a shape per lookup.
 */
type LookupRow = {
  id?: unknown
  name?: string
  label?: string
  actionType?: string
  measureType?: string
  volume?: string
  unit?: string
  materialType?: string
  materialName?: string
  methodType?: string
  methodName?: string
  zoneName?: string
  periodInterval?: string
  periodPer?: unknown
}

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === ''

/** Reads one meta lookup off the loosely-typed bag, always as an array. */
const lookupRows = (lookups: unknown, key: string): LookupRow[] => {
  const rows = (lookups as Record<string, unknown> | null | undefined)?.[key]
  return Array.isArray(rows) ? (rows as LookupRow[]) : []
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined

/** Default formatter for most values. */
export const formatDefault = (value: unknown): string => (isEmptyValue(value) ? '' : String(value))

export const formatBoolean = (value: unknown): string => (value ? 'Yes' : 'No')

export const formatInvoiceStatus = (value: unknown): string => {
  if (!value) return ''
  const display = INVOICE_STATUS_DISPLAY as Record<string, { text?: string } | undefined>
  return display[String(value)]?.text ?? String(value)
}

// --- Lookup-based formatters ---

/** Resolves an action by id or by case-insensitive label, returning the raw string when neither hits. */
export const formatAction = (value: unknown, lookups?: unknown): string => {
  if (isEmptyValue(value)) return ''

  const actions = lookupRows(lookups, 'actions')

  const idLike = (candidate: unknown): string | number | undefined => {
    if (!candidate) return undefined
    if (typeof candidate === 'string' || typeof candidate === 'number') return candidate
    const record = asRecord(candidate)
    if (!record) return undefined
    return (record.id ?? record.value ?? record.actionId ?? record.actionTypeId) as string | number | undefined
  }

  const labelLike = (candidate: unknown): string | undefined => {
    if (!candidate) return undefined
    if (typeof candidate === 'string') return candidate
    const record = asRecord(candidate)
    if (!record) return undefined
    return (record.actionType ?? record.name ?? record.label) as string | undefined
  }

  const id = idLike(value)
  if (!isEmptyValue(id)) {
    const byId = actions.find(action => action.id === id)
    if (byId) return byId.actionType || byId.name || byId.label || String(id)
  }

  const label = labelLike(value)
  if (label) {
    const normalized = label.trim().toLowerCase()
    if (normalized) {
      const byLabel = actions.find(action =>
        [action.actionType, action.name, action.label].filter(Boolean).some(c => String(c).trim().toLowerCase() === normalized)
      )
      // Falls through to the raw string rather than "Unknown" — an unrecognised label is still the
      // best thing to show.
      if (byLabel) return byLabel.actionType || byLabel.name || byLabel.label || label
      return label
    }
  }

  if (typeof value === 'string') return value
  return `Unknown (${String(value)})`
}

export const formatMeasure = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const measure = lookupRows(lookups, 'measures').find(row => row.id === value)
  if (!measure) return `Unknown (${String(value)})`
  const volume = measure.volume || ''
  const unit = measure.unit || ''
  if (volume && unit) return `${volume} ${unit}`
  return measure.measureType || measure.name || `Unknown (${String(value)})`
}

export const formatMaterial = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const material = lookupRows(lookups, 'materials').find(row => row.id === value)
  if (!material) return `Unknown (${String(value)})`
  return material.materialType || material.materialName || material.name || `Unknown (${String(value)})`
}

export const formatMethod = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const method = lookupRows(lookups, 'methods').find(row => row.id === value)
  if (!method) return `Unknown (${String(value)})`
  return method.methodType || method.methodName || method.name || `Unknown (${String(value)})`
}

export const formatPricingZone = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const zone = lookupRows(lookups, 'pricingZones').find(row => row.id === value)
  if (!zone) return `Unknown (${String(value)})`
  return zone.zoneName || zone.name || zone.label || `Unknown (${String(value)})`
}

export const formatTaxZone = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const zone = lookupRows(lookups, 'taxZones').find(row => row.id === value)
  if (!zone) return `Unknown (${String(value)})`
  return zone.zoneName || zone.name || zone.label || `Unknown (${String(value)})`
}

export const formatPricingPeriod = (value: unknown, lookups?: unknown): string => {
  if (value === null || value === undefined) return ''
  const period = lookupRows(lookups, 'pricingPeriods').find(row => row.id === value)
  if (!period) return `Unknown (${String(value)})`
  if (period.periodInterval === 'Instance') return ''
  try {
    return getPricingPeriod(period.periodPer, period.periodInterval)
  } catch {
    return `${period.periodPer || ''} ${period.periodInterval || ''}`.trim()
  }
}

export const formatMoney = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return ''
  // HHDisplayMoney's propTypes say string, but its callers pass cents and NumericFormat takes both.
  return <HHDisplayMoney formatToDollars={false} value={value as string} />
}

/** Date-only formatter for ISO strings or Date objects, in the legacy MM/dd/yyyy shape. */
export const formatDateOnly = (value: unknown): string => {
  if (isEmptyValue(value)) return ''
  try {
    return formatDateToFEFormatDateFns(value instanceof Date ? value : createDateFromString(value))
  } catch {
    return String(value)
  }
}
